/**
 * Marketplace cart — lightweight multi-experience packaging.
 * Items are fully-configured booking payloads saved by the widget's
 * "Add to Cart & Keep Browsing" button. Each item is held for 10 minutes
 * (no inventory reserved). Payment is chained: one Stripe checkout per
 * operator, one after another, via /cart/continue.
 */

const CART_KEY = 'bf_itinerary_v1';
const DONE_KEY = 'bf_itinerary_done_v1';
const HOLD_MS = 10 * 60 * 1000;
const PENDING_GRACE_MS = 30 * 60 * 1000;

const API = process.env.REACT_APP_BACKEND_URL;

const emit = () => {
  try { window.dispatchEvent(new CustomEvent('bf-itinerary-changed')); } catch (e) { /* SSR */ }
};

const save = (items) => {
  try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch (e) { /* full */ }
  emit();
};

const notExpired = (i) => {
  const now = Date.now();
  if ((i.expiresAt || 0) > now) return true;
  // Keep items that are mid-payment a while longer so /cart/continue can resolve them
  return !!(i.pendingCode && (i.pendingAt || 0) > now - PENDING_GRACE_MS);
};

export const getItinerary = () => {
  let items = [];
  try {
    items = JSON.parse(localStorage.getItem(CART_KEY) || '[]');
  } catch (e) { items = []; }
  if (!Array.isArray(items)) items = [];
  const fresh = items.filter(notExpired);
  if (fresh.length !== items.length) save(fresh);
  return fresh;
};

export const itineraryCount = () => getItinerary().length;

export const addToItinerary = (item) => {
  const items = getItinerary();
  items.push({
    ...item,
    id: `itin_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    addedAt: Date.now(),
    expiresAt: Date.now() + HOLD_MS,
  });
  save(items);
  return items.length;
};

export const removeFromItinerary = (id) => {
  const items = getItinerary().filter((i) => i.id !== id);
  save(items);
  return items;
};

export const clearItinerary = () => {
  try { localStorage.removeItem(CART_KEY); } catch (e) { /* noop */ }
  emit();
};

export const msRemaining = (item) => Math.max(0, (item.expiresAt || 0) - Date.now());

export const markPending = (id, code, checkoutUrl) => {
  const items = getItinerary().map((i) =>
    i.id === id ? { ...i, pendingCode: code, pendingCheckoutUrl: checkoutUrl, pendingAt: Date.now() } : i
  );
  save(items);
};

export const clearPending = (id) => {
  const items = getItinerary().map((i) => {
    if (i.id !== id) return i;
    const { pendingCode, pendingCheckoutUrl, pendingAt, ...rest } = i;
    return rest;
  });
  save(items);
};

// ── Paid bookings from this cart session (for the /cart/continue recap) ──
export const getDone = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DONE_KEY) || '[]');
    return Array.isArray(d) ? d : [];
  } catch (e) { return []; }
};

export const clearDone = () => {
  try { localStorage.removeItem(DONE_KEY); } catch (e) { /* noop */ }
};

// Move the cart item matching this confirmation code into the done list.
// Returns the done entry (or null if the item was not found).
export const resolvePaid = (code) => {
  if (!code) return null;
  const items = getItinerary();
  const paid = items.find((i) => i.pendingCode === code);
  const done = getDone();
  if (done.some((d) => d.code === code)) return done.find((d) => d.code === code);
  const entry = {
    code,
    isPackage: !!(paid?.type === 'package' || code.startsWith('PK-')),
    serviceName: paid?.display?.serviceName || '',
    orgName: paid?.display?.orgName || '',
    total: paid?.display?.total,
    symbol: paid?.display?.symbol || '',
    date: paid?.display?.date || '',
    displayTime: paid?.display?.displayTime || '',
    ts: Date.now(),
  };
  done.push(entry);
  try { localStorage.setItem(DONE_KEY, JSON.stringify(done)); } catch (e) { /* noop */ }
  if (paid) save(items.filter((i) => i.id !== paid.id));
  return entry;
};

// ── Chained payment: create the booking + redirect to its Stripe checkout ──
// Package cart items ({type:'package'}) go through the package orchestrator
// (one combined checkout for all components) — everything else per-booking.
export const payForItem = async (item) => {
  // Shopper cancelled at Stripe and retried — reuse the still-fresh session
  if (item.pendingCheckoutUrl && Date.now() - (item.pendingAt || 0) < 20 * 60 * 1000) {
    window.location.href = item.pendingCheckoutUrl;
    return;
  }
  const isPackage = item.type === 'package';
  const res = await fetch(
    isPackage ? `${API}/api/v2/public/package-bookings` : `${API}/api/public/bookings`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item.payload),
    }
  );
  if (!res.ok) {
    let msg = isPackage
      ? 'One of the package experiences is no longer available. Remove the package and rebuild it.'
      : 'This time slot is no longer available. Remove it and pick another time.';
    try {
      const err = await res.json();
      if (typeof err.detail === 'string') msg = err.detail;
      else if (err.detail?.message) msg = err.detail.message;
    } catch (e) { /* keep default */ }
    throw new Error(msg);
  }
  const data = await res.json();
  if (!data.checkoutUrl) throw new Error('Could not start payment. Please try again.');
  markPending(item.id, (isPackage ? data.packageCode : data.booking?.confirmationCode) || '', data.checkoutUrl);
  window.location.href = data.checkoutUrl;
};

// Soft overlap detection — informational only, never blocks (day passes,
// flexible stays and family-split bookings are all legitimate overlaps)
const toMinutes = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(':').map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};

export const findOverlaps = (items) => {
  const overlaps = new Set();
  for (let a = 0; a < items.length; a++) {
    for (let b = a + 1; b < items.length; b++) {
      const A = items[a].display || items[a];
      const B = items[b].display || items[b];
      if (!A.date || !B.date || A.date !== B.date) continue;
      const sA = toMinutes(A.time);
      const sB = toMinutes(B.time);
      if (sA === null || sB === null) continue;
      const eA = sA + (Number(A.durationMinutes) || 60);
      const eB = sB + (Number(B.durationMinutes) || 60);
      if (sA < eB && sB < eA) {
        overlaps.add(items[a].id);
        overlaps.add(items[b].id);
      }
    }
  }
  return overlaps;
};
